/* iter55u — Extracted from AppShell.jsx.
 *
 * Bundles every top-level dialog into a single component so AppShell
 * only needs to pass `dialogs`, `open`, `close`, and the review-viewer
 * state. Pure presentational — no fetch/state of its own.
 */
import React from 'react';
import { SettingsDialog } from './dialogs/SettingsDialog';
import { AboutDialog } from './dialogs/AboutDialog';
import { NewProjectDialog } from './dialogs/NewProjectDialog';
import { BuyTokensDialog } from './dialogs/BuyTokensDialog';
import { ExportDialog } from './dialogs/ExportDialog';
import { ImportManuscriptDialog } from './dialogs/ImportManuscriptDialog';
import { ImportSynopsisDialog } from './dialogs/ImportSynopsisDialog';
import SaveAsDialog from './chrome/SaveAsDialog';
import { PublishToBookstoreDialog } from './dialogs/PublishToBookstoreDialog';
import { MyRoyaltiesDialog } from './dialogs/MyRoyaltiesDialog';
import { HelpDialog } from './dialogs/HelpDialog';
import ImportThreeActSynopsisDialog from './dialogs/ImportThreeActSynopsisDialog';
import GenerateSynopsisFromLoglineDialog from './dialogs/GenerateSynopsisFromLoglineDialog';
import SynopsisBuilderDialog from './dialogs/SynopsisBuilderDialog';
import WorldBuilderDialog from './dialogs/WorldBuilderDialog';
import RevisionModePanel from './panels/editor/RevisionModePanel';
import { usePW } from '@/lib/store';
import IsbnManagerDialog from './dialogs/IsbnManagerDialog';
import CanvaCoverDialog from './dialogs/CanvaCoverDialog';
import AccountConnectionsDialog from './dialogs/AccountConnectionsDialog';
import TemplatesPickerDialog from './dialogs/TemplatesPickerDialog';
import { CoachingBookDialog } from './dialogs/CoachingBookDialog';
import { EditorReviewRequestDialog } from './dialogs/EditorReviewRequestDialog';
import { EditorReviewViewer } from './dialogs/EditorReviewViewer';
import MyInboxPanel from './MyInboxPanel';   // iter-p3-c
import DemoTourModal from './DemoTourModal';
import WelcomeOverlay from './dialogs/WelcomeOverlay';
import AudiobookDialog from './dialogs/AudiobookDialog';   // iter-ab1
import NarrationStudioDialog from './dialogs/NarrationStudioDialog';  // ElevenLabs narrator studio
import ManuscriptBulkToolsDialog from './dialogs/ManuscriptBulkToolsDialog';  // Bulk export/import
import ScriptConverterDialog from './dialogs/ScriptConverterDialog';            // Screenplay/teleplay converter
import KdpMetadataDialog from './dialogs/KdpMetadataDialog';                    // KDP Metadata form

/** Small helper that turns `dialogs[key]` + open/close into the shape
 *  every shadcn dialog expects. Cuts ~20 lines of ternary noise. */
const bind = (dialogs, open, close, key) => ({
  open: dialogs[key],
  onOpenChange: (v) => (v ? open(key) : close(key)),
});

export default function AppDialogs({
  dialogs, open, close, viewingReviewId, setViewingReviewId,
  pendingSynopsisFile, onDropFile,
}) {
  const { project, loadProject, activeChapterId, chapters } = usePW();
  const activeChapter = chapters?.find((c) => c.id === activeChapterId) || null;
  // 2026-10 — Deep-link bridge: the BYOK paywall nudge (and anything
  // else) can dispatch `loom:open-settings` with `{ tab }` to open
  // Settings directly on a given tab instead of always landing on
  // My Account.
  const [settingsInitialTab, setSettingsInitialTab] = React.useState('account');
  React.useEffect(() => {
    const handler = (e) => {
      setSettingsInitialTab(e.detail?.tab || 'account');
      open('settings');
    };
    window.addEventListener('loom:open-settings', handler);
    return () => window.removeEventListener('loom:open-settings', handler);
  }, [open]);
  return (
    <>
      <SettingsDialog {...bind(dialogs, open, close, 'settings')} initialTab={settingsInitialTab} onBuyTokens={() => open('tokens')} onOpenInbox={() => open('myInbox')} />
      <AboutDialog {...bind(dialogs, open, close, 'about')} />
      <NewProjectDialog {...bind(dialogs, open, close, 'newProject')} />
      <BuyTokensDialog {...bind(dialogs, open, close, 'tokens')} />
      <ExportDialog {...bind(dialogs, open, close, 'export')} />
      <SaveAsDialog {...bind(dialogs, open, close, 'saveAs')} />
      <ImportManuscriptDialog {...bind(dialogs, open, close, 'importManuscript')} />
      <ImportSynopsisDialog
        {...bind(dialogs, open, close, 'importSynopsis')}
        initialFile={pendingSynopsisFile}
      />
      <PublishToBookstoreDialog {...bind(dialogs, open, close, 'publish')} />
      <MyRoyaltiesDialog {...bind(dialogs, open, close, 'royalties')} />
      <HelpDialog {...bind(dialogs, open, close, 'help')} />
      <ImportThreeActSynopsisDialog
        open={dialogs.importThreeActSynopsis}
        onClose={() => close('importThreeActSynopsis')}
      />
      <GenerateSynopsisFromLoglineDialog
        open={dialogs.loglineToSynopsis}
        onClose={() => close('loglineToSynopsis')}
      />
      {/* iter55g23 — 11-step Synopsis Builder. */}
      <SynopsisBuilderDialog
        open={dialogs.buildSynopsis}
        onClose={() => close('buildSynopsis')}
        project={project}
        onSaved={(p) => { if (p?.id) loadProject(p.id, { silent: true }); }}
      />
      {/* iter55g24 — Mode-switch UX: World Builder + Revision panel. */}
      <WorldBuilderDialog
        open={dialogs.worldBuilder}
        onClose={() => close('worldBuilder')}
        projectId={project?.id}
      />
      <RevisionModePanel
        open={dialogs.revisionMode}
        onClose={() => close('revisionMode')}
        projectId={project?.id}
        chapterId={activeChapterId}
        chapterTitle={activeChapter?.title || (activeChapter ? `Chapter ${activeChapter.number}` : '')}
      />
      <IsbnManagerDialog {...bind(dialogs, open, close, 'isbn')} />
      <CanvaCoverDialog {...bind(dialogs, open, close, 'canva')} />
      <AudiobookDialog {...bind(dialogs, open, close, 'audiobook')} />
      <NarrationStudioDialog
        open={dialogs.narrationStudio}
        onOpenChange={(v) => (v ? open('narrationStudio') : close('narrationStudio'))}
      />
      <AccountConnectionsDialog {...bind(dialogs, open, close, 'connections')} />
      <TemplatesPickerDialog
        {...bind(dialogs, open, close, 'templates')}
        onNewBlank={() => { close('templates'); open('newProject'); }}
      />
      <CoachingBookDialog {...bind(dialogs, open, close, 'bookCoaching')} />
      <EditorReviewRequestDialog
        {...bind(dialogs, open, close, 'requestEditorReview')}
        onOpenReview={(rid) => { close('requestEditorReview'); setViewingReviewId(rid); }}
      />
      <EditorReviewViewer
        open={Boolean(viewingReviewId)}
        onOpenChange={(v) => { if (!v) setViewingReviewId(null); }}
        reviewId={viewingReviewId}
      />
      <DemoTourModal />
      {/* iter-p3-c — Author's personal inbox surface. */}
      <MyInboxPanel {...bind(dialogs, open, close, 'myInbox')} />
      {/* iter55x — Welcome overlay for first-time / genuinely-empty projects. */}
      <WelcomeOverlay
        onImportSynopsis={() => open('importSynopsis')}
        onImportManuscript={() => open('importManuscript')}
        onDropFile={(file) => {
          onDropFile?.(file);
          open('importSynopsis');
        }}
      />
      {/* Bulk manuscript export / import — accessible from File menu */}
      <ManuscriptBulkToolsDialog {...bind(dialogs, open, close, 'bulkManuscripts')} />
      {/* Convert novel to Screenplay / Teleplay / Stage Play */}
      <ScriptConverterDialog
        open={dialogs.scriptConverter}
        onClose={() => close('scriptConverter')}
        project={project}
      />
      {/* KDP Metadata — AI-assisted publishing metadata form */}
      <KdpMetadataDialog
        open={dialogs.kdpMetadata}
        onOpenChange={(v) => (v ? open('kdpMetadata') : close('kdpMetadata'))}
        projectId={project?.id}
      />
    </>
  );
}
